/**
 * Client-side fuzzy matching for player names.
 * Runs entirely in-memory against data already in Redux — never calls the search API.
 * Output feeds into the Haiku correction pipeline as candidate suggestions.
 */

import Fuse from 'fuse.js';
import { JaroWinklerDistance } from 'natural/lib/natural/distance';
import daitchMokotoff from 'talisman/phonetics/daitch-mokotoff';
import { store } from '../providers/storeProvider/config/store.ts';

/**
 * Extract potential player name tokens from transcribed text.
 * Splits on common command words, keeping multi-word sequences that could be names.
 * @param {string} text
 * @returns {string[]}
 */
const extractNameTokens = (text) => {
  // Remove common command phrases to isolate name-like segments
  const commandWords = /\b(show|me|stats|for|find|search|how|did|perform|compare|add|from|of|the|and|with|get|player|players|team)\b/gi;
  const cleaned = text.replace(commandWords, '|').replace(/[^\w\s|À-ž'-]/g, '');

  // Split on pipe delimiters and whitespace clusters, then group adjacent capitalized words
  return cleaned
    .split('|')
    .map(s => s.trim())
    .filter(s => s.length > 1);
};

/**
 * Generate fuzzy match candidates for transcribed text against a roster.
 * @param {string} rawText - Raw Whisper transcription
 * @param {Array<{name: string}>} roster - Player roster from Redux
 * @returns {Array<{transcribed: string, candidates: string[], topScore: number}>}
 */
export const generateFuzzyCandidates = (rawText, roster) => {
  if (!roster || roster.length === 0) return [];

  const rosterNames = roster.map(p => p.name).filter(Boolean);
  const nameTokens = extractNameTokens(rawText);
  const results = [];

  for (const token of nameTokens) {
    if (token.length < 2) continue;

    const candidates = [];

    // 1. Exact match (case-insensitive)
    const exact = rosterNames.find(n => n.toLowerCase() === token.toLowerCase());
    if (exact) {
      candidates.push({ name: exact, score: 1.0, method: 'exact' });
    }

    // 2. Jaro-Winkler distance (best for short person names)
    for (const name of rosterNames) {
      const score = JaroWinklerDistance(token.toLowerCase(), name.toLowerCase());
      if (score > 0.75 && !candidates.some(c => c.name === name)) {
        candidates.push({ name, score, method: 'jaro-winkler' });
      }
    }

    // 3. Phonetic match — Daitch-Mokotoff Soundex (Slavic name specialist)
    try {
      const tokenCodes = daitchMokotoff(token);
      for (const name of rosterNames) {
        if (candidates.some(c => c.name === name)) continue;
        // Compare against each part of the player name (first + last)
        const nameParts = name.split(/\s+/);
        for (const part of nameParts) {
          const partCodes = daitchMokotoff(part);
          if (tokenCodes.some(tc => partCodes.includes(tc))) {
            candidates.push({ name, score: 0.75, method: 'phonetic-dm' });
            break;
          }
        }
      }
    } catch {
      // Skip phonetic matching on error
    }

    // 4. Fuse.js fuzzy search (catch-all)
    const fuse = new Fuse(rosterNames.map(n => ({ name: n })), {
      keys: ['name'],
      threshold: 0.4,
      includeScore: true,
    });
    const fuseResults = fuse.search(token);
    for (const result of fuseResults.slice(0, 3)) {
      const name = result.item.name;
      if (!candidates.some(c => c.name === name)) {
        // Fuse score is 0 (perfect) to 1 (no match), invert it
        candidates.push({ name, score: 1 - result.score, method: 'fuzzy' });
      }
    }

    // Sort by score descending and keep top 3
    candidates.sort((a, b) => b.score - a.score);
    const topCandidates = candidates.slice(0, 3);

    if (topCandidates.length > 0 && topCandidates[0].score > 0.6) {
      results.push({
        transcribed: token,
        candidates: topCandidates.map(c => c.name),
        topScore: topCandidates[0].score,
      });
    }
  }

  return results;
};

/**
 * Apply fuzzy corrections to the raw text by replacing transcribed tokens with best matches.
 * @param {string} rawText
 * @param {Array<{transcribed: string, candidates: string[]}>} fuzzyCandidates
 * @returns {string}
 */
export const applyFuzzyCorrections = (rawText, fuzzyCandidates) => {
  let corrected = rawText;
  for (const { transcribed, candidates } of fuzzyCandidates) {
    if (candidates.length > 0) {
      // Replace the transcribed token with the top candidate (case-insensitive)
      const regex = new RegExp(transcribed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      corrected = corrected.replace(regex, candidates[0]);
    }
  }
  return corrected;
};

/**
 * Get the best available player roster from Redux state.
 * Checks multiple state locations and returns a merged, deduplicated list.
 * @returns {Array<{name: string}>}
 */
export const getAvailableRoster = () => {
  const state = store.getState();
  const names = new Set();
  const roster = [];

  const addPlayer = (p) => {
    // players_list uses display_name; other sources use name
    const n = p?.display_name || p?.name;
    if (n && !names.has(n)) { names.add(n); roster.push({ name: n }); }
  };

  // Events Explorer team roster (from players_list — has display_name)
  const teamRoster = state.eventsExplorer?.search?.teamRoster;
  if (Array.isArray(teamRoster)) teamRoster.forEach(addPlayer);

  // Events Explorer selected players
  const selected = state.eventsExplorer?.selectedPlayers;
  if (Array.isArray(selected)) selected.forEach(addPlayer);

  // Team page players (from players_list — has display_name)
  const teamPlayers = state.team?.teamStatistic?.players_list;
  if (Array.isArray(teamPlayers)) teamPlayers.forEach(addPlayer);

  // Match players
  const matchPlayers = state.match?.matchPlayers;
  if (Array.isArray(matchPlayers)) matchPlayers.forEach(addPlayer);

  return roster;
};
